import {
    MARKETING_COMPARISON_LABELS,
    MarketingComparisonMode,
} from '@/lib/api/marketing/marketing-dashboard-api'
import { useEffect, useState } from 'react'
import { MdCheck, MdCompareArrows } from 'react-icons/md'
import { formatRange } from './marketing-format'

interface ComparisonSelectorProps {
    value: MarketingComparisonMode
    onChange: (mode: MarketingComparisonMode) => void
    startDate: string
    endDate: string
    onCustomDatesChange: (startDate: string, endDate: string) => void
    /** Intervalo que o backend resolveu para a comparação (o que de fato está na tela). */
    resolved: { startDay: string; endDay: string } | null
}

const MODES: MarketingComparisonMode[] = ['previous', 'year', 'custom', 'none']

/**
 * Com o que o período é comparado. Mesmo rascunho do seletor de período do financeiro:
 * as datas do intervalo personalizado só valem quando o operador confirma (o
 * calendário nativo troca o valor já ao navegar de mês).
 */
export default function ComparisonSelector({
    value,
    onChange,
    startDate,
    endDate,
    onCustomDatesChange,
    resolved,
}: ComparisonSelectorProps) {
    const [draftStart, setDraftStart] = useState(startDate)
    const [draftEnd, setDraftEnd] = useState(endDate)
    useEffect(() => setDraftStart(startDate), [startDate])
    useEffect(() => setDraftEnd(endDate), [endDate])

    const complete = !!draftStart && !!draftEnd
    const inverted = complete && draftStart > draftEnd
    const changed = draftStart !== startDate || draftEnd !== endDate
    const canApply = complete && !inverted && changed
    const apply = () => {
        if (canApply) onCustomDatesChange(draftStart, draftEnd)
    }

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 text-xs font-medium text-base-content/60 mr-1">
                    <MdCompareArrows className="text-base" />
                    Comparar com
                </span>
                {MODES.map(mode => (
                    <button
                        key={mode}
                        type="button"
                        onClick={() => onChange(mode)}
                        className={`btn btn-sm ${value === mode ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                    >
                        {MARKETING_COMPARISON_LABELS[mode]}
                    </button>
                ))}
                {resolved && value !== 'custom' && (
                    <span className="text-xs text-base-content/60">{formatRange(resolved)}</span>
                )}
            </div>

            {value === 'custom' && (
                <div className="flex flex-wrap items-end gap-3 bg-base-100 border border-base-300 rounded-lg p-3">
                    <div className="form-control">
                        <label className="label py-1">
                            <span className="label-text text-xs font-medium">Comparar de</span>
                        </label>
                        <input
                            type="date"
                            className="input input-bordered input-sm"
                            value={draftStart}
                            max={draftEnd || undefined}
                            onChange={e => setDraftStart(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && apply()}
                        />
                    </div>
                    <div className="form-control">
                        <label className="label py-1">
                            <span className="label-text text-xs font-medium">Até</span>
                        </label>
                        <input
                            type="date"
                            className="input input-bordered input-sm"
                            value={draftEnd}
                            min={draftStart || undefined}
                            onChange={e => setDraftEnd(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && apply()}
                        />
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-secondary gap-1"
                        disabled={!canApply}
                        onClick={apply}
                    >
                        <MdCheck className="text-base" />
                        Aplicar
                    </button>
                    {!complete && (
                        <span className="text-xs text-base-content/60 pb-2">
                            Selecione as duas datas e clique em Aplicar.
                        </span>
                    )}
                    {inverted && (
                        <span className="text-xs text-error pb-2">
                            A data inicial não pode ser depois da final.
                        </span>
                    )}
                </div>
            )}
        </div>
    )
}
